// The launch form's judge and budget fields (TOM-120, ADR 0248), shared by the
// launch page and a run's re-grade (TOM-257, ADR 0254): the owner's default
// judges or others added by id, no judge (the phrase heuristic), each judge
// grading twice, a judge of the graded model's own vendor, and the required
// budget in USD.

import { useState } from 'react';

import { EvalBadge } from './EvalBits';
import { formatUsd } from './format';
import {
  EVAL_LAUNCH_MAX_JUDGES,
  modelIdInput,
  withId,
  type EvalLaunchForm,
  type EvalLaunchOptions,
} from './launch';
import '../../styles/admin-evals-launch.css';

/** A model or judge typed by id, checked before it joins the list. */
export function AddId({
  label,
  placeholder,
  disabled,
  testId,
  onAdd,
}: {
  label: string;
  placeholder: string;
  disabled: boolean;
  testId: string;
  onAdd: (id: string) => void;
}) {
  const [text, setText] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  function add() {
    const r = modelIdInput(text);
    if ('problem' in r) {
      setProblem(r.problem);
      return;
    }
    setProblem(null);
    setText('');
    onAdd(r.id);
  }
  return (
    <div className="eval-launch-add">
      <label>
        <span className="eval-launch-label">{label}</span>
        <input
          type="text"
          value={text}
          maxLength={200}
          placeholder={placeholder}
          disabled={disabled}
          data-testid={testId}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
        />
      </label>
      <button
        type="button"
        className="admin-button-secondary"
        disabled={disabled || text.trim().length === 0}
        onClick={add}
      >
        Add
      </button>
      {problem !== null ? <p className="eval-error-text eval-sub">{problem}</p> : null}
    </div>
  );
}

/** What the judge fields read and change: a launch form's, or a re-grade form's. */
export type EvalJudgeChoice = Pick<
  EvalLaunchForm,
  'extraJudges' | 'judges' | 'noJudge' | 'judgeTwice' | 'allowSameFamilyJudge'
>;

export function EvalJudgeFields({
  judges: defaults,
  form,
  set,
  testIdPrefix = 'eval-launch',
}: {
  /** The owner's default judges, as the launch options list them. */
  judges: EvalLaunchOptions['judges'];
  form: EvalJudgeChoice;
  set: (patch: Partial<EvalJudgeChoice>) => void;
  testIdPrefix?: string;
}) {
  const judgeIds = [...defaults.map((j) => j.id), ...form.extraJudges];
  const toggle = (id: string, on: boolean) =>
    judgeIds.filter((x) => (x === id ? on : form.judges.includes(x)));
  return (
    <fieldset className="eval-launch-section" data-testid={`${testIdPrefix}-judges`}>
      <legend>Judges</legend>
      <label className="eval-launch-option">
        <input
          type="checkbox"
          checked={form.noJudge}
          data-testid={`${testIdPrefix}-no-judge`}
          onChange={(e) => set({ noJudge: e.target.checked })}
        />
        <span className="eval-launch-option-text">
          <span>No judge: grade with the phrase heuristic</span>
          <span className="admin-muted eval-sub">
            No judge call, but the run cannot be a baseline.
          </span>
        </span>
      </label>
      {!form.noJudge ? (
        <>
          <ul className="eval-launch-options">
            {judgeIds.map((id) => {
              const o = defaults.find((j) => j.id === id);
              return (
                <li key={id}>
                  <label className="eval-launch-option" data-testid={`${testIdPrefix}-judge-${id}`}>
                    <input
                      type="checkbox"
                      checked={form.judges.includes(id)}
                      onChange={(e) => set({ judges: toggle(id, e.target.checked) })}
                    />
                    <span className="eval-launch-option-text">
                      <code>{id}</code>
                      <span className="eval-launch-option-tags">
                        <span className="admin-muted eval-sub">
                          {o !== undefined ? 'Default judge' : 'Added by id'}
                        </span>
                        {o !== undefined && !o.configured ? (
                          <EvalBadge
                            tone="warn"
                            title="Its provider key is not set: the models it would grade are not graded, and fail"
                          >
                            No provider key
                          </EvalBadge>
                        ) : null}
                      </span>
                    </span>
                  </label>
                  {o === undefined ? (
                    <button
                      type="button"
                      className="admin-link-button"
                      onClick={() =>
                        set({
                          extraJudges: form.extraJudges.filter((x) => x !== id),
                          judges: form.judges.filter((x) => x !== id),
                        })
                      }
                    >
                      Remove
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
          <AddId
            label="Another judge, by id"
            placeholder="openrouter:vendor/model or anthropic:claude-…"
            disabled={form.judges.length >= EVAL_LAUNCH_MAX_JUDGES}
            testId={`${testIdPrefix}-add-judge`}
            onAdd={(id) =>
              set({
                extraJudges: judgeIds.includes(id) ? form.extraJudges : [...form.extraJudges, id],
                judges: withId(form.judges, id),
              })
            }
          />
          <label className="eval-launch-option">
            <input
              type="checkbox"
              checked={form.judgeTwice}
              data-testid={`${testIdPrefix}-twice`}
              onChange={(e) => set({ judgeTwice: e.target.checked })}
            />
            <span>Each judge grades every reply twice (measures judge flips)</span>
          </label>
          <label className="eval-launch-option">
            <input
              type="checkbox"
              checked={form.allowSameFamilyJudge}
              data-testid={`${testIdPrefix}-same-vendor`}
              onChange={(e) => set({ allowSameFamilyJudge: e.target.checked })}
            />
            <span>Allow a judge from the graded model&apos;s own vendor</span>
          </label>
          <p className="admin-muted eval-sub">
            A judge never grades its own vendor unless allowed: a model with no eligible judge is
            not graded, and fails.
          </p>
        </>
      ) : null}
    </fieldset>
  );
}

/** A run's budget, at the head of the estimate it drives: required, in USD. */
export function EvalBudgetField({
  maxBudgetUsd,
  value,
  onChange,
  testId,
}: {
  maxBudgetUsd: number;
  value: string;
  onChange: (value: string) => void;
  testId: string;
}) {
  return (
    <div className="eval-launch-section eval-launch-budget">
      <label>
        <span className="eval-launch-label">Budget in USD (required)</span>
        <span className="eval-launch-money">
          <span aria-hidden="true">$</span>
          <input
            type="number"
            min={0.01}
            max={maxBudgetUsd}
            step={0.01}
            inputMode="decimal"
            value={value}
            placeholder="e.g. 2.00"
            required
            data-testid={testId}
            onChange={(e) => onChange(e.target.value)}
          />
        </span>
      </label>
      <p className="admin-muted eval-sub">
        The run stops before a case that could cross it, and ends &quot;budget exceeded&quot; with
        what it finished. At most {formatUsd(maxBudgetUsd)}.
      </p>
    </div>
  );
}
